// Cell parsing + payload build/validation for the program editor, split
// out of ./programEdit to keep both files under the 500-line cap. This
// holds the reps/intensity cell parsers and the matrix→weeks pivot +
// zod validation that turns a composer Draft into a NewProgram payload.
// Imports only TYPES from ./programEdit (erased at runtime), so there is
// no runtime import cycle; ./programEdit re-exports these symbols for the
// existing import paths. Keep it free of React and any UI import.

import {
  NewProgram,
  type NewProgram as NewProgramT,
  type SetIntensity as SetIntensityT,
} from "./schemas/program.js";
import type { IntensityKind, Draft } from "./programEdit.js";
import { toCanonicalKg } from "./units.js";
import {
  setAuthoredLocale,
  type AuthoredText as AuthoredTextT,
} from "./authoredText.js";
import type { UnitsPreference } from "./schemas/profile.js";
import {
  amrapWithTimeError,
  buildWorkForCell,
  timeAxisError,
} from "./programEditTimeAxis.js";

// Reps cell token: "5" (exact) or "5+" (AMRAP — 5 is the floor, the
// athlete does at least that). The composer's reps fields speak this
// syntax; buildPayload turns the trailing + into SetPrescription.amrap.
export const parseRepsToken = (
  v: string,
): { reps: number; amrap: boolean } | null => {
  const m = /^\s*(\d{1,2})\s*(\+)?\s*$/.exec(v);
  if (!m) return null;
  const reps = parseInt(m[1]!, 10);
  if (!Number.isFinite(reps) || reps > 50) return null;
  return { reps, amrap: m[2] === "+" };
};

export const parseFloatLoose = (v: string): number | null => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// Stable machine codes for a build/validation failure. `reason` stays a
// human English string (Sentry breadcrumb / debug); `code` is what the UI
// maps to an i18n key (program.errCode.<code>, generic fallback) so the
// error banner never shows raw English. Mirrors the coach.errCode shape.
export type IntensityErrorCode =
  | "invalidIntensityValue"
  | "weightOutOfRange"
  | "pctOutOfRange"
  | "rpeOutOfRange"
  | "rirOutOfRange"
  | "unknownIntensityKind";

export type BuildReasonCode =
  | IntensityErrorCode
  | "sessionNameEmpty"
  | "slotNoExercise"
  | "slotNoSets"
  | "slotAllWeeksInactive"
  | "rowWidthMismatch"
  | "invalidReps"
  | "invalidSeconds"
  | "amrapWithTime"
  | "groupKindMixed"
  | "schemaInvalid"
  // plan (single) mode only
  | "noSession"
  | "planNameEmpty"
  | "planNoExercises";

// Build a SetIntensity from the row's kind + cell value. Returns an
// { error, code } when the cell value is missing or out of range for the
// kind, so buildPayload can flag the offending cell.
export const buildIntensity = (
  kind: IntensityKind,
  rawValue: string,
  // The unit the CELL is expressed in — i.e. the unit the draft was seeded
  // in, not whatever the profile says right now (useProgramEditor captures
  // it at seed time so the two directions can never disagree). Default
  // "kg" makes the conversion an identity, so a caller that doesn't pass
  // it behaves exactly as before: a forgotten callsite degrades to today's
  // behaviour instead of silently scaling the family's prescriptions by 2.2.
  cellUnit: UnitsPreference = "kg",
): SetIntensityT | { error: string; code: IntensityErrorCode } => {
  if (kind === "none") return { kind: "none" };
  // history is value-less like none: "use the athlete's last-logged
  // weight". No numeric cell to parse.
  if (kind === "history") return { kind: "history" };
  const n = parseFloatLoose(rawValue);
  if (n == null)
    return { error: "Invalid intensity value", code: "invalidIntensityValue" };
  if (kind === "absolute") {
    // Convert BEFORE the bounds check: the range is expressed in kg (the
    // storage unit), so checking the typed lb figure against it would
    // reject a legal 2000 lb and accept an out-of-range one.
    const kg = toCanonicalKg(n, cellUnit);
    if (kg < -500 || kg > 2000)
      return { error: "Weight out of range", code: "weightOutOfRange" };
    return { kind: "absolute", weightKg: kg };
  }
  if (kind === "percent-of-max") {
    if (n < 0 || n > 200)
      return { error: "Percentage out of range", code: "pctOutOfRange" };
    return { kind: "percent-of-max", pct: n };
  }
  if (kind === "rpe") {
    if (n < 1 || n > 10)
      return { error: "RPE must be 1..10", code: "rpeOutOfRange" };
    return { kind: "rpe", target: n };
  }
  if (kind === "rir") {
    if (n < 0 || n > 10)
      return { error: "RIR must be 0..10", code: "rirOutOfRange" };
    return { kind: "rir", target: n };
  }
  return { error: "Unknown intensity kind", code: "unknownIntensityKind" };
};

// Kinds that carry no numeric value: the cell renders reps only and
// buildIntensity is never consulted for them.
export const VALUELESS_KINDS: ReadonlySet<IntensityKind> = new Set([
  "none",
  "history",
]);

// The value a cell falls back to when it switches into a value-bearing kind
// with nothing in it. Each seed sits inside its own kind's valid range (RIR
// is 0..10, not kg), which is why this can't be a single constant. Both the
// program editor's setRowKind and the composer's row-kind switch read it —
// they used to carry a copy each, and a copy that drifts prices an athlete's
// set wrong.
export const seedForIntensityKind = (kind: IntensityKind): string =>
  kind === "rpe" ? "8" : kind === "rir" ? "2" : kind === "percent-of-max" ? "70" : "60";

export type BuildResult =
  | { ok: true; payload: NewProgramT }
  | { ok: false; reason: string; code: BuildReasonCode };

// `cellUnit` is the unit the draft's absolute cells are expressed in — see
// buildIntensity. It must be the SAME unit programToDraft seeded them with.
export const buildPayload = (
  draft: Draft,
  cellUnit: UnitsPreference = "kg",
): BuildResult => {
  for (const session of draft.sessions) {
    if (session.name.trim().length === 0)
      return { ok: false, reason: "Session name empty", code: "sessionNameEmpty" };
    for (const slot of session.slots) {
      if (slot.exerciseId.trim().length === 0)
        return { ok: false, reason: "Slot has no exercise", code: "slotNoExercise" };
      if (slot.setRows.length === 0)
        return { ok: false, reason: "Slot has no sets", code: "slotNoSets" };
      if (slot.inactiveWeeks.every(Boolean))
        return {
          ok: false,
          reason: "Slot inactive on every week",
          code: "slotAllWeeksInactive",
        };
      for (const row of slot.setRows) {
        if (row.cells.length !== draft.cycleWeeks)
          return { ok: false, reason: "Row width mismatch", code: "rowWidthMismatch" };
        for (let w = 0; w < row.cells.length; w++) {
          // Inactive weeks emit sets: [] — their cells are placeholders
          // and never reach the payload, so don't validate them.
          if (slot.inactiveWeeks[w]) continue;
          const cell = row.cells[w]!;
          if (parseRepsToken(cell.reps) == null)
            return { ok: false, reason: "Invalid reps", code: "invalidReps" };
          if (timeAxisError(row.workKind, cell.workDurationSec, cell.restSec))
            return {
              ok: false,
              reason: "Invalid work/rest seconds",
              code: "invalidSeconds",
            };
          if (amrapWithTimeError(row.workKind, cell.reps))
            return {
              ok: false,
              reason: "AMRAP floor on a clock-bounded bout",
              code: "amrapWithTime",
            };
          if (!VALUELESS_KINDS.has(row.intensityKind)) {
            const intensity = buildIntensity(
              row.intensityKind,
              cell.intensityValue,
              cellUnit,
            );
            if ("error" in intensity)
              return { ok: false, reason: intensity.error, code: intensity.code };
          }
        }
      }
    }
  }

  // Reject mixed kinds inside a single group id before save — same
  // defense-in-depth check WorkoutLog runs. The optgroup-based UI
  // can't normally produce a mismatch, but a stored program re-loaded
  // with legacy / hand-edited data could; surface it instead of
  // silently round-tripping.
  for (const session of draft.sessions) {
    const seenKind = new Map<string, string>();
    for (const slot of session.slots) {
      if (!slot.group) continue;
      const prior = seenKind.get(slot.group.id);
      if (prior && prior !== slot.group.kind) {
        return {
          ok: false,
          reason: `mixed kinds for group ${slot.group.id}`,
          code: "groupKindMixed",
        };
      }
      seenKind.set(slot.group.id, slot.group.kind);
    }
  }

  // Sanitise singleton groups per session before serialising. A session
  // where the coach marked one slot as "Superset A" without adding a
  // partner shouldn't leak the id into storage. Mirrors WorkoutLog's
  // and PlansCard's pre-save passes.
  const cleaned = draft.sessions.map((session) => {
    const counts = new Map<string, number>();
    for (const slot of session.slots) {
      if (slot.group) {
        counts.set(slot.group.id, (counts.get(slot.group.id) ?? 0) + 1);
      }
    }
    const deSingletoned = session.slots.map((slot) =>
      slot.group && (counts.get(slot.group.id) ?? 0) < 2
        ? { ...slot, group: null }
        : slot,
    );
    // Force symmetric supersets on save (Antenore 2026-06-01, inbox 11f01866
    // pt 2): a superset renders as interleaved rounds, so members with
    // different set counts leave orphaned rounds in the logger. Pad each
    // member up to its group's longest by repeating its own last set row (set
    // count is uniform across a slot's weeks, so we pad every week). Never
    // trims — dropping a prescribed row would lose volume. Mirrors the
    // resolver's read-time guard; together they keep stored + resolved data
    // symmetric.
    const groupMax = new Map<string, number>();
    for (const slot of deSingletoned) {
      if (!slot.group) continue;
      const n = slot.setRows.length;
      if (n > (groupMax.get(slot.group.id) ?? 0)) groupMax.set(slot.group.id, n);
    }
    return {
      ...session,
      slots: deSingletoned.map((slot) => {
        if (!slot.group) return slot;
        const target = groupMax.get(slot.group.id) ?? 0;
        if (slot.setRows.length === 0 || slot.setRows.length >= target) return slot;
        const setRows = [...slot.setRows];
        while (setRows.length < target) {
          const last = setRows[setRows.length - 1]!;
          setRows.push({ ...last, cells: last.cells.map((c) => ({ ...c })) });
        }
        return { ...slot, setRows };
      }),
    };
  });

  // PE-21 — LOSSLESS ROUND-TRIP FOR TRANSLATIONS.
  //
  // A program PATCH replaces the whole object, so emitting the plain
  // string the editor happens to be showing would DELETE every other
  // language on save. Each edited string is grafted back into the
  // language the draft is writing, leaving the rest of the stored value
  // untouched. Same rule as `variantsRaw` for multi-variant cadence
  // (§3.2) — the editor must never destroy what it can't display.
  //
  // ⛔ Naked strings stay naked: a program that was never translated
  // does not silently become an object the first time it is saved.
  const graft = (
    raw: AuthoredTextT | undefined,
    text: string,
  ): AuthoredTextT | undefined => {
    const trimmed = text.trim();
    // ⛔ The shape check comes FIRST. `editLocale` is now null for every
    // program written before PE-21, so testing it first would let a
    // per-language object be collapsed to the one string on screen —
    // silently, with no error, destroying every other language in that
    // field. Whether we know the writing language decides what we WRITE,
    // never whether we preserve what is already there.
    if (raw === undefined || typeof raw === "string")
      return trimmed || undefined;
    if (!draft.editLocale) return raw;
    return setAuthoredLocale(raw, draft.editLocale, trimmed);
  };

  const payload: NewProgramT = {
    name: graft(draft.nameRaw, draft.name) ?? draft.name.trim(),
    description: graft(draft.descriptionRaw, draft.description),
    // PE-21 — record the language the text was WRITTEN in, so the read
    // chain can go reader → author → any available instead of falling
    // through to English. Absent when the draft has no writing language:
    // a program from before PE-21 whose language nobody has declared
    // stays undeclared rather than being guessed.
    // ⛔ `declaredAuthorLocale`, NOT `editLocale`: the tab in view is not
    // a claim about the program. Browsing the French and saving must not
    // make an Italian program French for every reader in the app.
    ...(draft.declaredAuthorLocale
      ? { authorLocale: draft.declaredAuthorLocale }
      : {}),
    cycleWeeks: draft.cycleWeeks,
    sessionsPerWeek: draft.sessionsPerWeek,
    status: draft.status,
    // ME-3: targets are no longer carried on the program — they live on the
    // athlete's AthleteTargets doc now, so the save path neither sends nor
    // preserves them.
    // Preserve existing assignees; the chip-input emails ride alongside
    // via assignedToEmails (server resolves + dedupes).
    assignedTo: draft.assignedTo,
    ...(draft.assigneeEmails.length > 0
      ? { assignedToEmails: draft.assigneeEmails }
      : {}),
    sessions: cleaned.map((s) => ({
      id: s.id || undefined,
      name: graft(s.nameRaw, s.name) ?? s.name.trim(),
      notes: graft(s.notesRaw, s.notes),
      slots: s.slots.map((slot, slotIdx) => {
        // Pivot rows[]→ weeks[]: for each week, take the w-th cell of
        // every row and assemble that week's sets[]. Inactive weeks
        // emit sets: [] (slot doesn't run that week — schema §WeekPlan).
        const weeks = Array.from({ length: draft.cycleWeeks }, (_, w) => ({
          sets: slot.inactiveWeeks[w]
            ? []
            : slot.setRows.map((row) => {
                const cell = row.cells[w]!;
                const tok = parseRepsToken(cell.reps) ?? {
                  reps: 0,
                  amrap: false,
                };
                const intensity =
                  row.intensityKind === "none"
                    ? { kind: "none" as const }
                    : (buildIntensity(
                        row.intensityKind,
                        cell.intensityValue,
                        cellUnit,
                      ) as SetIntensityT);
                const notes = graft(
                  row.notesRawPerCell?.[w],
                  row.notesPerCell[w] ?? "",
                );
                const { amrapAllowed, ...time } = buildWorkForCell(
                  row.workKind,
                  cell.workDurationSec,
                  cell.restSec,
                );
                return {
                  reps: tok.reps,
                  ...(tok.amrap && amrapAllowed ? { amrap: true } : {}),
                  intensity,
                  ...time,
                  notes,
                };
              }),
        }));
        // Lossless round-trip (§3.2): a stored slot's variants survive
        // verbatim — the editor's exercise pick grafts onto variants[0]
        // (keeping its cadence/phaseOffset), extra variants re-emit
        // untouched. Fresh slots synthesise the single every-week variant.
        const v0 = slot.variantsRaw[0];
        const variants = v0
          ? [
              {
                ...v0,
                exerciseId: slot.exerciseId,
                exerciseName: slot.exerciseName,
              },
              ...slot.variantsRaw.slice(1),
            ]
          : [
              {
                exerciseId: slot.exerciseId,
                exerciseName: slot.exerciseName,
                cadence: "every-week" as const,
                phaseOffset: 0,
              },
            ];
        return {
          id: slot.id || undefined,
          position: slotIdx,
          variants,
          weeks,
          instructions: graft(slot.instructionsRaw, slot.instructions),
          amrapLastSet: slot.amrapLastSet || undefined,
          maxAttempt: slot.maxAttempt || undefined,
          rpeEnabled: slot.rpeEnabled || undefined,
          rirEnabled: slot.rirEnabled || undefined,
          // Group only persists when ≥2 slots in the same session share
          // the id; otherwise resolveProgramSession would drop it on
          // resolve. The singleton filter above already nulled lone groups.
          ...(slot.group
            ? {
                group: {
                  id: slot.group.id,
                  kind: slot.group.kind,
                  // Carried verbatim (HI-1): the editor cannot author a
                  // cadence yet, so re-emitting is the only way a stored
                  // one survives a save from this screen.
                  ...(slot.group.cadence !== undefined
                    ? { cadence: slot.group.cadence }
                    : {}),
                },
              }
            : {}),
        };
      }),
    })),
  };
  const validated = NewProgram.safeParse(payload);
  if (!validated.success) {
    const first = validated.error.issues[0];
    const path = first?.path.join(".") ?? "?";
    return {
      ok: false,
      reason: `${path}: ${first?.message ?? ""}`,
      code: "schemaInvalid",
    };
  }
  return { ok: true, payload: validated.data };
};
